// src/vite/utils/logger.ts - Simplified XCon Logger Integration

import {CoreLogger, LoggerConfig} from "@xcons/common";

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error', 'silent'] as const;
export type LogLevel = typeof LOG_LEVELS[number];

/**
 * Logger interface
 */
export interface Logger {
    debug: (message: string, ...args: any[]) => void;
    info: (message: string, ...args: any[]) => void;
    warn: (message: string, ...args: any[]) => void;
    error: (message: string, ...args: any[]) => void;
    group?: (title: string) => void;
    groupEnd?: () => void;
    time?: (label: string) => void;
    timeEnd?: (label: string) => void;
}

/**
 * Create logger instance with XCon Logger integration
 */
export function createLogger(loggerConfig?: Partial<LoggerConfig> & { logLevel?: string }): Logger {
    const fallbackLogLevel = loggerConfig?.logLevel || 'info';

    // Try to use XCon LoggerUtils if available (Node.js environment only)
    if (typeof window === 'undefined') {
        try {
            // Dynamic import to avoid issues when LoggerUtils is not available


            // Configure XCon Logger
            if (loggerConfig) {
                const {logLevel, ...xconConfig} = loggerConfig;
                CoreLogger.configure(xconConfig);
            }

            const xconLogger = CoreLogger.getInstance();

            return {
                debug: (message: string, ...args: any[]) => xconLogger.debug(message, ...args),
                info: (message: string, ...args: any[]) => xconLogger.info(message, ...args),
                warn: (message: string, ...args: any[]) => xconLogger.warn(message, ...args),
                error: (message: string, ...args: any[]) => xconLogger.error(message, ...args),
                group: (title: string) => xconLogger.group(title),
                groupEnd: () => xconLogger.groupEnd(),
                time: (label: string) => xconLogger.time(label),
                timeEnd: (label: string) => xconLogger.timeEnd(label)
            };
        } catch (error) {
            // XCon LoggerUtils not available, fallback to simple logger
            console.warn('[XCon Vite Plugin] XCon LoggerUtils not available, using fallback logger');
        }
    }

    // Fallback logger implementation
    const levels = {
        silent: -1,
        debug: 0,
        info: 1,
        warn: 2,
        error: 3
    };

    const currentLevel = levels[fallbackLogLevel as keyof typeof levels] ?? 1;

    const shouldLog = (level: string): boolean => {
        if (currentLevel === -1) return false; // silent mode
        const messageLevel = levels[level as keyof typeof levels] ?? 1;
        return messageLevel >= currentLevel;
    };

    return {
        debug: (message: string, ...args: any[]) => {
            if (shouldLog('debug')) {
                console.log(`[XCon Plugin] 🔧 ${message}`, ...args);
            }
        },
        info: (message: string, ...args: any[]) => {
            if (shouldLog('info')) {
                console.info(`[XCon Plugin] ℹ️ ${message}`, ...args);
            }
        },
        warn: (message: string, ...args: any[]) => {
            if (shouldLog('warn')) {
                console.warn(`[XCon Plugin] ⚠️ ${message}`, ...args);
            }
        },
        error: (message: string, ...args: any[]) => {
            if (shouldLog('error')) {
                console.error(`[XCon Plugin] ❌ ${message}`, ...args);
            }
        },
        group: (title: string) => {
            if (shouldLog('debug') && typeof console.group === 'function') {
                console.group(`[XCon Plugin] ${title}`);
            }
        },
        groupEnd: () => {
            if (shouldLog('debug') && typeof console.groupEnd === 'function') {
                console.groupEnd();
            }
        },
        time: (label: string) => {
            if (shouldLog('debug') && typeof console.time === 'function') {
                console.time(`[XCon Plugin] ${label}`);
            }
        },
        timeEnd: (label: string) => {
            if (shouldLog('debug') && typeof console.timeEnd === 'function') {
                console.timeEnd(`[XCon Plugin] ${label}`);
            }
        }
    };
}